import { useEffect, useState } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import { type Product, type ProductBundle } from '@/domain/Product';
import useTranslations from '@/widgets/hooks/useTranslations';
import * as ProductService from '@/widgets/services/ProductService.ts';
import useRoute from '@/widgets/hooks/useRoute.tsx';
import BundledItems from '../BundledItems/BundledItems.tsx';

export type ResolvedBundleProducts = Array<
  null | ProductBundle['contents'][number]
>;

export type BundleProductsListProps = {
  product: Product;
};
export default function BundleProductsList({
  product
}: BundleProductsListProps) {
  const { t } = useTranslations();
  const { market, channel, locale } = useRoute();
  const { bundle, groupedProducts } = product;
  if (!bundle && !groupedProducts) {
    return null;
  }

  const headingText = t(
    'productList.quickView.assortments.whatsIncludedHeading'
  );
  const errorLoadingProductText = t(
    'productList.quickView.assortments.errorLoadingProduct'
  );

  // stopping users seeing duplicated products or order change while loading
  const [isReady, setIsReady] = useState(false);

  // TODO: remove any 'any' type.
  // Also this signal is not reactive, so, same than useState
  // (as per comment https://dsu-bitbucket.nestle.biz/projects/NNHQE/repos/nespresso-site-partials/pull-requests/928/overview?commentId=826904)
  const bundleProducts = useSignal<any>(bundle?.contents || []);

  useEffect(() => {
    (async () => {
      const controller = new AbortController();

      if (bundle?.contents?.length) {
        // HMC - our configurator
        const resolvedProducts = await ProductService.resolveLazyProducts(
          market,
          channel,
          locale,
          bundle.contents
        );

        bundleProducts.value = [...resolvedProducts.values()];
      } else if (groupedProducts?.length) {
        // ECAPI - coming from nessoft
        const resolvedProducts =
          await ProductService.resolveGroupedProductsFromECAPI(
            market,
            channel,
            locale,
            groupedProducts
          );

        bundleProducts.value = [...resolvedProducts.values()];
      } else {
        bundleProducts.value = [];
      }

      setIsReady(true);

      return () => controller.abort();
    })();
  }, [market, channel, locale, bundle?.contents, groupedProducts?.length]);

  // stopping users seeing duplicated products or order change while loading
  if (!isReady) return null;

  return (
    <BundledItems
      headingText={headingText}
      bundleProducts={bundleProducts.value}
      errorLoadingProductText={errorLoadingProductText}
    />
  );
}
